import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import SegmentAccordion from '../components/segments/SegmentAccordion'
import SyncProgressDialog from '../components/studies/SyncProgressDialog'
import FiltersBar from '../components/studies/FiltersBar'
import type { DateType } from '../components/studies/FiltersBar'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  CalendarToday as CalendarIcon,
  PersonAdd as AssignIcon,
  PersonRemove as UnassignIcon,
  Upload as UploadIcon,
  DeleteForever as DeleteIcon,
  Language as WebIcon,
  CheckCircle as CheckCircleIcon,
  HourglassTop as HourglassTopIcon,
  Share as ShareIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  DesktopWindows as DesktopIcon
} from '@mui/icons-material'
import { Checkbox } from '@mui/material'
import apiClient, { medicApi } from '../services/api'
import ShareLinkDialog from '../components/studies/ShareLinkDialog'
import { Study, StudiesResponse, StudyFilters, UserWithProfile } from '../types/api'
import toast from 'react-hot-toast'
import { Autocomplete } from '@mui/material'
import ReportEditor from '../components/ReportEditor'
import { useAuth } from '../contexts/AuthContext'
import { eventBus, EVENTS } from '../utils/eventBus'
import { openOHIFViewer } from '../utils/ohifViewer'
// import { useIncomingStudies } from '../hooks/useIncomingStudies'  // Disabled for direct debug
import { PageHeader } from '../components/ui'
import UploadExternalDICOMDialog from '../components/patients/UploadExternalDICOMDialog'
import QuickAddPatientForm from '../components/patients/QuickAddPatientForm'
import { 
  isFinalized, 
  isInProgress, 
  getStatusDisplay, 
  StudyWorkflowStatus,
  getEffectiveStatus
} from '../constants/studyStatus'
// getAccessToken nu mai este necesar - cookie-based auth

const StudiesPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [studies, setStudies] = useState<Study[]>([])
  const [loading, setLoading] = useState(true)
  
  // ✨ State pentru Medic Coordonator
  const [isCoordinator, setIsCoordinator] = useState(false)
  const [coordinatedInstitutionIds, setCoordinatedInstitutionIds] = useState<string[]>([])
  
  // ✨ Incoming Studies - DIRECT FETCH pentru debugging
  const [incomingStudies, setIncomingStudies] = useState<any[]>([])
  
  // ✨ Trigger pentru refresh lista principală când un studiu se completează
  const [studyCompleteCounter, setStudyCompleteCounter] = useState(0)
  
  // Fetch incoming studies + WebSocket pentru actualizări instant
  useEffect(() => {
    console.log('🔥 [StudiesPage] Setting up incoming studies tracking...')
    
    const fetchIncoming = async () => {
      try {
        const response = await fetch('/api/v1/internal/incoming-studies?limit=50')
        if (response.ok) {
          const data = await response.json()
          console.log('🔥 [StudiesPage] Incoming studies:', data.studies?.length || 0)
          setIncomingStudies(data.studies || [])
        }
      } catch (err) {
        console.error('🔥 [StudiesPage] Fetch error:', err)
      }
    }
    
    // Initial fetch
    fetchIncoming()
    
    // WebSocket pentru actualizări instant
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/api/v1/ws/incoming-studies`
    let ws: WebSocket | null = null
    let reconnectTimer: NodeJS.Timeout | null = null
    
    const connectWs = () => {
      console.log('🔌 [WS] Connecting to', wsUrl)
      ws = new WebSocket(wsUrl)
      
      ws.onopen = () => {
        console.log('🔌 [WS] Connected!')
        fetchIncoming() // Refresh on connect
      }
      
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          console.log('🔌 [WS] Message:', msg.type)
          
          if (msg.type === 'incoming_study' || msg.type === 'study_update') {
            fetchIncoming() // Refresh incoming list
          }
          
          if (msg.type === 'study_complete') {
            console.log('🔌 [WS] Study complete, refreshing main list...')
            fetchIncoming() // Refresh incoming list
            // Trigger refresh al listei principale după scurt delay pentru a da timp sincronizării
            setTimeout(() => {
              setStudyCompleteCounter(c => c + 1)
            }, 1500)
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      ws.onclose = () => {
        console.log('🔌 [WS] Closed, reconnecting in 3s...')
        reconnectTimer = setTimeout(connectWs, 3000)
      }
      
      ws.onerror = () => {
        console.log('🔌 [WS] Error')
      }
    }
    
    connectWs()
    
    // Fallback polling every 10 seconds
    const interval = setInterval(fetchIncoming, 10000)
    
    return () => {
      clearInterval(interval)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (ws) ws.close()
    }
  }, [])
  
  // State pentru filtru instituții - MOVED UP pentru a fi disponibil în useMemo
  const [selectedInstitution, setSelectedInstitution] = useState<string>("all")
  const [availableInstitutions, setAvailableInstitutions] = useState<Array<{id: string, name: string, is_coordinated?: boolean}>>([])
  
  // ✨ Combină studiile incoming cu cele normale - incoming la început
  // ✨ IMPORTANT: Incoming studies vizibile DOAR pentru operatori și tehnicieni,
  //    și DOAR pentru instituțiile la care sunt asociați
  const combinedStudies = useMemo(() => {
    // Verifică dacă utilizatorul poate vedea incoming studies
    // Admin, operator și tehnician pot vedea incoming studies
    const canSeeIncoming = user?.role === 'admin' || user?.role === 'operator' || user?.role === 'tehnician'
    
    // Obține ID-urile instituțiilor la care utilizatorul are acces
    const userInstitutionIds = new Set(availableInstitutions.map(inst => inst.id))
    
    // Convertește incoming studies la formatul Study cu is_incoming flag
    // Filtrează și pe instituție pentru operatori/tehnicieni
    const filteredIncoming = canSeeIncoming 
      ? incomingStudies.filter(inc => {
          // Dacă studiul nu are institution_id, nu îl afișăm
          if (!inc.institution_id) return false
          // Verifică dacă utilizatorul are acces la această instituție
          if (!userInstitutionIds.has(inc.institution_id)) return false
          // Dacă e selectată o instituție specifică în dropdown, filtrăm și pe ea
          if (selectedInstitution !== 'all' && inc.institution_id !== selectedInstitution) return false
          return true
        })
      : [] // Alți utilizatori nu văd incoming studies
    
    const incomingAsStudies = filteredIncoming.map((inc) => ({
      id: inc.id,
      study_uid: inc.study_uid,
      patient_name: inc.patient_name?.replace('^', ' ') || 'Necunoscut',
      patient_id: inc.patient_id || '',
      patient_cnp: inc.patient_cnp || null,
      study_date: inc.study_date,
      study_description: inc.study_description || '',
      modality: inc.modality || '',
      modalities_list: Array.isArray(inc.modalities_list) 
        ? inc.modalities_list 
        : (inc.modalities_list || inc.modality || '').split(',').filter(Boolean),
      series_count: inc.series_count || 0,
      instances_count: inc.instances_count || 0,
      is_incoming: true,
      incoming_instances: inc.instances_count,
      // Câmpuri necesare pentru render (valori default)
      status: 'incoming' as const,
      priority: 'normal' as const,
      pacs: null,
      assigned_doctor: null,
      institution: null,
      institution_id: inc.institution_id || null,  // ID-ul instituției
      institution_name: inc.institution_name || inc.pacs_identifier || null,  // Instituția din backend
      created_at: inc.first_instance_at || inc.study_date || '',
      updated_at: inc.last_instance_at || inc.first_instance_at || inc.study_date || '',
    }))
    
    // Filtrează studiile deja sincronizate (au același study_uid)
    const existingUids = new Set(studies.map(s => s.study_uid))
    const newIncoming = incomingAsStudies.filter(inc => !existingUids.has(inc.study_uid))
    
    console.log('[StudiesPage] Combined studies:', {
      incoming: newIncoming.length,
      regular: studies.length,
      total: newIncoming.length + studies.length,
      canSeeIncoming,
      userRole: user?.role,
      userInstitutions: userInstitutionIds.size,
      selectedInstitution
    })
    
    // Incoming la început, apoi studiile normale
    return [...newIncoming, ...studies]
  }, [incomingStudies, studies, user?.role, availableInstitutions, selectedInstitution])

  // ✨ NEW - State pentru segment accordion expand
  const [expandedStudyId, setExpandedStudyId] = useState<string | null>(null)
  
  // ✨ NEW - State pentru segment counts (cache)
  const [segmentCounts, setSegmentCounts] = useState<Record<string, number>>({})
  
  // ✨ SCALABLE SYNC - State pentru sync progress dialog
  const [syncProgressOpen, setSyncProgressOpen] = useState(false)
  const [syncTaskIds, setSyncTaskIds] = useState<Array<{task_id: string, pacs_name: string}>>([])
  
  // ✨ NEW - Fetch segment counts pentru multiple studies
  const fetchSegmentCounts = async (studyIds: string[]) => {
    try {
      // Fetch counts în paralel pentru toate studiile
      const countPromises = studyIds.map(async (studyId) => {
        try {
          const response = await apiClient.get(`/segments/?study_id=${studyId}`)
          return { studyId, count: response.data?.length || 0 }
        } catch {
          return { studyId, count: 0 }
        }
      })
      
      const results = await Promise.all(countPromises)
      
      // Update state cu counts
      const countsMap: Record<string, number> = {}
      results.forEach(({ studyId, count }) => {
        countsMap[studyId] = count
      })
      setSegmentCounts(countsMap)
    } catch (err) {
      console.error('Error fetching segment counts:', err)
    }
  }

  // Event listener pentru actualizări de status din alte pagini
  useEffect(() => {
    const handleStudyStatusUpdate = (data: { studyId: string; newStatus: string; reason: string }) => {
      console.log('🔄 Study status update event:', data)
      updateStudyInList(data.studyId, { status: data.newStatus })
      
      // Toast pentru feedback vizual
      if (data.reason === 'report_amended') {
        toast.success(`Status studiu actualizat: ${data.newStatus}`)
      }
    }

    eventBus.on(EVENTS.STUDY_STATUS_UPDATED, handleStudyStatusUpdate)

    return () => {
      eventBus.off(EVENTS.STUDY_STATUS_UPDATED, handleStudyStatusUpdate)
    }
  }, [])

  // ✨ WebSocket hooks moved after function definitions (see below)

  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState<StudyFilters>({})
  const [showFilters, setShowFilters] = useState(true) // Default true pentru a afișa filtrele
  
  // State pentru filtrele noi
  const [dateType, setDateType] = useState<DateType>('study_date')
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null)
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [selectedModalities, setSelectedModalities] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState<string>('')
  
  // State pentru selecție studii (checkbox)
  const [selectedStudies, setSelectedStudies] = useState<Set<string>>(new Set())
  
  // ✨ NEW - Sort state pentru "Data Intrare RIS" (default DESC - cea mai nouă prima)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [sortBy, setSortBy] = useState<'created_at' | 'patient_name'>('created_at')
  
  // State pentru filtrare medic (doar pentru admin/operator)
  const [filterDoctors, setFilterDoctors] = useState<UserWithProfile[]>([])
  const [loadingFilterDoctors, setLoadingFilterDoctors] = useState(false)
  const [filterDoctorInput, setFilterDoctorInput] = useState('')
  const filterDoctorSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  
  // ✨ OPTIMIZARE: Debounce pentru search principal (400ms)
  // Previne request-uri excesive la fiecare tastă apăsată
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Aplică filtrele din URL parameters la încărcarea paginii
  useEffect(() => {
    const urlQuery = searchParams.get('q')
    const urlStatus = searchParams.get('status')
    const urlModality = searchParams.get('modality')
    const urlPriority = searchParams.get('priority')
    const urlInstitution = searchParams.get('institution_id')
    const urlAssignedTo = searchParams.get('assigned_to')
    const urlStudyId = searchParams.get('studyId')
    const urlOpenViewer = searchParams.get('openViewer')
    
    const urlFilters: StudyFilters = {}
    
    if (urlQuery) urlFilters.q = urlQuery
    if (urlStatus) urlFilters.status = urlStatus
    if (urlModality) urlFilters.modality = urlModality
    if (urlPriority) urlFilters.priority = urlPriority
    if (urlInstitution) urlFilters.institution_id = urlInstitution
    if (urlAssignedTo) urlFilters.assigned_to = urlAssignedTo
    
    // Aplică filtrele dacă există parametri în URL
    if (Object.keys(urlFilters).length > 0) {
      setFilters(urlFilters)
      setShowFilters(true) // Deschide secțiunea de filtre dacă există filtre în URL
    }
    
    // Dacă există studyId și openViewer, deschide direct ZenRIS Viewer
    if (urlStudyId && urlOpenViewer === 'true' && studies.length > 0) {
      const study = studies.find(s => s.id === urlStudyId)
      if (study && study.study_uid) {
        void openOHIFViewer(study.study_uid)
        // Curăță URL-ul
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('studyId')
        newParams.delete('openViewer')
        window.history.replaceState({}, '', `${window.location.pathname}?${newParams}`)
      }
    }
  }, [searchParams, studies])
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null)
  const [showStudyDetails, setShowStudyDetails] = useState(false)
  const [searchDoctors, setSearchDoctors] = useState<UserWithProfile[]>([])
  const [loadingDoctors, setLoadingDoctors] = useState(false)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [studyToAssign, setStudyToAssign] = useState<Study | null>(null)
  const [selectedDoctor, setSelectedDoctor] = useState<string>('')
  const [showUnassignDialog, setShowUnassignDialog] = useState(false)
  const [studyToUnassign, setStudyToUnassign] = useState<Study | null>(null)
  const [unassignReason, setUnassignReason] = useState('')
  const [showReportEditor, setShowReportEditor] = useState(false)
  const [currentReport, setCurrentReport] = useState<any>(null) // Report data for editor
  const [reportEditorReadOnly, setReportEditorReadOnly] = useState(false) // ✨ View-only mode for coordinators
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null) // PDF viewer URL for finalized reports
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [studyToUpload, setStudyToUpload] = useState<Study | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [showHardDeleteDialog, setShowHardDeleteDialog] = useState(false)
  const [studyToHardDelete, setStudyToHardDelete] = useState<Study | null>(null)
  const [hardDeleteReason, setHardDeleteReason] = useState('')
  const [hardDeleting, setHardDeleting] = useState(false)
  
  
  // State pentru ShareLinkDialog (long-lived sharing)
  const [showShareLinkDialog, setShowShareLinkDialog] = useState(false)
  const [studyToShare, setStudyToShare] = useState<Study | null>(null)
  
  // State pentru Quick Actions (DICOM upload și Add Patient)
  const [showDicomUploadDialog, setShowDicomUploadDialog] = useState(false)
  const [showAddPatientDialog, setShowAddPatientDialog] = useState(false)
  
  // State pentru opțiunile dinamice de filtrare
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([])
  const [availableModalities, setAvailableModalities] = useState<string[]>([])
  const [availablePriorities, setAvailablePriorities] = useState<string[]>([])

  // Statusuri pentru filtrare - doar workflow_status (ZenRIS 5.0)
  // Backend face dual-filtering pe ambele coloane (workflow_status + status legacy)
  const DEFAULT_STATUSES = [
    'new',        // Nou, nealocat
    'assigned',   // Alocat medicului
    'in_progress', // Raport în lucru
    'completed',  // Finalizat
    'external',   // Upload manual (CD/USB)
    'historical', // Istoric/comparație
  ]

  // Extrage opțiunile unice pentru filtrare din datele studiilor
  const updateFilterOptions = (studiesData: Study[]) => {
    // Debug: să vedem ce date avem
    console.log('Debug - primul studiu:', studiesData[0])
    
    // Extrage statusurile unice (filtrează undefined și string-uri goale)
    const statuses = [
      ...new Set(
        studiesData
          .map(study => study.status)
          .filter((status): status is string => Boolean(status))
      ),
    ]
    // Afișăm DOAR statusurile noi (workflow_status) - nu adăugăm statusurile vechi din date
    // Backend face dual-filtering deci filtrele noi vor găsi și studiile vechi
    setAvailableStatuses(DEFAULT_STATUSES)
    
    // Extrage modalitățile unice - verifică atât modality cât și modalities_list
    const modalitiesSet = new Set<string>()
    studiesData.forEach(study => {
      // Adaugă din modality (string)
      if (study.modality) {
        modalitiesSet.add(study.modality)
      }
      // Adaugă din modalities_list (array)
      if (study.modalities_list && Array.isArray(study.modalities_list)) {
        study.modalities_list.forEach(mod => modalitiesSet.add(mod))
      }
    })
    const modalities = Array.from(modalitiesSet)
    // IMPORTANT: nu micșorăm lista când filtrăm
    setAvailableModalities(prev => Array.from(new Set([...prev, ...modalities])).sort())
    
    // Extrage prioritățile unice (filtrează undefined și string-uri goale)
    const priorities = [...new Set(studiesData.map(study => study.priority).filter((priority): priority is string => Boolean(priority)))]
    setAvailablePriorities(priorities.sort())
    
    // Debug: să vedem ce opțiuni am extras
    console.log('Debug - statuses:', statuses)
    console.log('Debug - modalities:', modalities)
    console.log('Debug - priorities:', priorities)
  }

  // Încarcă lista de instituții disponibile (doar cele cu PACS configurat)
  const loadInstitutions = async () => {
    try {
      const response = await apiClient.get('/tenants/institutions?has_pacs=true')
      setAvailableInstitutions(response.data || [])
    } catch (error: any) {
      console.error('Eroare la încărcarea instituțiilor:', error)
      // Setează lista goală în caz de eroare, dar lasă dropdown-ul vizibil
      setAvailableInstitutions([])
    }
  }

  // Încarcă instituțiile la montarea componentei
  useEffect(() => {
    loadInstitutions()
  }, [user?.role])

  // ✨ Încarcă informațiile de coordonator pentru medici
  useEffect(() => {
    const loadCoordinatorInfo = async () => {
      if (user?.role !== 'medic') {
        setIsCoordinator(false)
        setCoordinatedInstitutionIds([])
        return
      }
      
      try {
        const response = await apiClient.get('/users/me/coordinator-info')
        setIsCoordinator(response.data.is_coordinator || false)
        setCoordinatedInstitutionIds(
          (response.data.coordinated_institutions || []).map((i: any) => i.id)
        )
      } catch (error) {
        console.error('Error fetching coordinator info:', error)
        setIsCoordinator(false)
        setCoordinatedInstitutionIds([])
      }
    }
    
    loadCoordinatorInfo()
  }, [user?.role, user?.id])

  // Persistența în localStorage pentru instituția selectată
  React.useEffect(() => {
    localStorage.setItem('selectedInstitution', selectedInstitution)
  }, [selectedInstitution])

  // Încărcare din localStorage la inițializare
  React.useEffect(() => {
    const savedInstitution = localStorage.getItem('selectedInstitution')
    if (savedInstitution) setSelectedInstitution(savedInstitution)
  }, [])

  // Caută medicii cu search as you type
  const searchDoctorsFunction = async (query: string) => {
    try {
      setLoadingDoctors(true)
      
      if (query.length >= 3) {
        // Search as you type după 3 caractere
        const response = await apiClient.get(`/users/?role=medic&limit=20&q=${encodeURIComponent(query)}`)
        setSearchDoctors(response.data || [])
      } else if (query.length === 0) {
        // Încarcă primii 15 medici în ordine alfabetică când nu e nimic scris
        const response = await apiClient.get(`/users/?role=medic&limit=20`)
        setSearchDoctors(response.data || [])
      } else {
        // Nu face nimic pentru 1-2 caractere
        setSearchDoctors([])
      }
    } catch (error) {
      console.error('Eroare la căutarea medicilor:', error)
      toast.error('Eroare la căutarea medicilor')
    } finally {
      setLoadingDoctors(false)
    }
  }

  // Caută medicii pentru filtru (doar pentru admin/operator)
  const searchDoctorsForFilter = async (query: string) => {
    // Păstrează medicul selectat în options ca să nu dispară la re-fetch
    const selected = filters.doctor_id ? filterDoctors.find(d => d.id === filters.doctor_id) : undefined
    try {
      setLoadingFilterDoctors(true)
      
      if (query.length >= 3) {
        // Search as you type după 3 caractere
        const response = await apiClient.get(`/users/?role=medic&limit=20&q=${encodeURIComponent(query)}`)
        const doctors: UserWithProfile[] = response.data || []
        if (selected && !doctors.some(d => d.id === selected.id)) doctors.unshift(selected)
        setFilterDoctors(doctors)
      } else if (query.length === 0) {
        // Încarcă primii 15 medici în ordine alfabetică când nu e nimic scris
        const response = await apiClient.get(`/users/?role=medic&limit=20`)
        const doctors: UserWithProfile[] = response.data || []
        if (selected && !doctors.some(d => d.id === selected.id)) doctors.unshift(selected)
        setFilterDoctors(doctors)
      } else {
        // Nu face request pentru 1-2 caractere; păstrează (minim) medicul selectat
        setFilterDoctors(selected ? [selected] : [])
      }
    } catch (error) {
      console.error('Eroare la căutarea medicilor:', error)
      toast.error('Eroare la căutarea medicilor')
    } finally {
      setLoadingFilterDoctors(false)
    }
  }

  const scheduleSearchDoctorsForFilter = (query: string) => {
    if (filterDoctorSearchTimeoutRef.current) {
      clearTimeout(filterDoctorSearchTimeoutRef.current)
    }
    filterDoctorSearchTimeoutRef.current = setTimeout(() => {
      searchDoctorsForFilter(query)
    }, 250)
  }

  // Încarcă studiile
  const loadStudies = async () => {
    try {
      console.log('🔄 Încărcare studii...', { page, rowsPerPage, filters, dateRange, dateType })
      setLoading(true)
      setError(null)

      // Construiește parametrii pentru API call
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        size: rowsPerPage.toString()
      })
      
      // Adaugă filtrele doar dacă au valori (exclude string-uri goale și undefined)
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, value.toString())
        }
      })
      
      // ✨ NEW - Adaugă sort parameter (created_at sau patient_name)
      params.set('sort_by', sortBy)
      params.set('order', sortOrder)
      
      // Adaugă filtrul pentru instituție dacă este selectată
      if (selectedInstitution !== "all") {
        params.set('institution_id', selectedInstitution)
      }
      
      console.log('Debug - API URL:', `/studies/?${params}`)
      console.log('Debug - Filters:', filters)
      const response = await apiClient.get<StudiesResponse>(`/studies/?${params}`)
      console.log('✅ Studii încărcate:', response.data.studies.length, 'studii din', response.data.total, 'total')
      setStudies(response.data.studies)
      setTotal(response.data.total)
      
      // ✨ NEW - Fetch segment counts pentru toate studiile
      const studyIds = response.data.studies.map(s => s.id)
      await fetchSegmentCounts(studyIds)
      
      // Actualizează opțiunile de filtrare cu datele reale
      updateFilterOptions(response.data.studies)
    } catch (err: any) {
      console.error('❌ Eroare la încărcarea studiilor:', err)
      setError(err.response?.data?.detail || 'Eroare la încărcarea studiilor')
      toast.error('Eroare la încărcarea studiilor')
    } finally {
      setLoading(false)
    }
  }

  
  // ✨ NEW - Handler pentru toggle sort order pe Data Intrare RIS
  const handleDateHeaderClick = () => {
    setSortBy('created_at')
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc'
    setSortOrder(newOrder)
    setPage(0) // Reset la pagina 1
  }

  // ✨ NEW - Handler pentru sortare după nume pacient
  const handlePatientNameSort = () => {
    if (sortBy === 'patient_name') {
      // Dacă e deja sortat după nume, schimbă ordinea
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
    } else {
      // Dacă nu e sortat după nume, setează sortarea după nume (asc default)
      setSortBy('patient_name')
      setSortOrder('asc')
    }
    setPage(0) // Reset la pagina 1
  }
  // ✨ SCALABLE SYNC v2.0 - Background task cu progress tracking
  const syncStudiesFromPacs = async () => {
    // Verifică dacă este selectată o instituție specifică
    if (selectedInstitution === "all") {
      toast.error("Selectați o instituție specifică pentru sincronizare")
      return
    }

    try {
      setSyncing(true)
      setError(null)

      const institutionText = availableInstitutions.find(inst => inst.id === selectedInstitution)?.name || "instituția selectată"

      // Lansează background sync task
      const syncUrl = `/studies/sync?institution_id=${selectedInstitution}&background=true`
      const response = await apiClient.post(syncUrl)
      
      console.log('🚀 Sync task launched:', response.data)
      
      // Extract task IDs
      const tasks = response.data.tasks || []
      if (tasks.length === 0) {
        throw new Error('No sync tasks were created')
      }

      // Prepare task IDs for progress tracking
      const taskIds = tasks.map((task: any) => ({
        task_id: task.task_id,
        pacs_name: task.pacs_name
      }))

      // Show success notification
      toast.success(
        `Sincronizare inițiată pentru ${institutionText}! Urmăriți progresul în fereastra care se va deschide.`,
        { duration: 3000 }
      )

      // Open progress dialog
      setSyncTaskIds(taskIds)
      setSyncProgressOpen(true)
      setSyncing(false)  // Allow user to continue working

    } catch (err: any) {
      console.error('❌ Eroare la lansarea sincronizării:', err)
      const errorMsg = err.response?.data?.detail || 'Eroare la lansarea sincronizării'
      setError(errorMsg)
      toast.error(errorMsg)
      setSyncing(false)
    }
  }

  // Handle sync progress dialog close
  const handleSyncProgressClose = () => {
    setSyncProgressOpen(false)
    setSyncTaskIds([])
  }

  // Handle sync completion
  const handleSyncComplete = async () => {
    console.log('✅ Sync completed! Reloading studies...')
    
    // Show completion notification
    toast.success('Sincronizare finalizată cu succes!', {
      icon: '✅',
      duration: 3000
    })

    // Reload studies to show new/updated data
    await loadStudies()
    
    // Don't auto-close dialog - let user close manually by clicking "Închide"
  }


  // Nu mai încărcăm toți medicii la montare, doar când se caută

  // Încarcă studiile la montarea componentei și când se schimbă filtrele sau instituția
  // ✨ studyCompleteCounter forțează refresh când un studiu se finalizează via WebSocket
  useEffect(() => {
    loadStudies()
  }, [page, rowsPerPage, filters, selectedInstitution, sortOrder, sortBy, dateRange, dateType, studyCompleteCounter])

  // ✨ NEW - Încarcă lista de medici când se deschide panoul de filtre
  useEffect(() => {
    if (showFilters && user?.role && ['admin', 'operator'].includes(user.role) && filterDoctors.length === 0) {
      searchDoctorsForFilter('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters, user?.role])

  // Cleanup pentru debounce timeout
  // Cleanup pentru timeout-uri la unmount
  useEffect(() => {
    return () => {
      if (filterDoctorSearchTimeoutRef.current) {
        clearTimeout(filterDoctorSearchTimeoutRef.current)
      }
      // ✨ OPTIMIZARE: Curăță și search debounce timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])


  // Gestionează schimbarea paginii
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  // Gestionează schimbarea numărului de rânduri per pagină
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Gestionează căutarea
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    console.log('Debug - search value:', value)
    setFilters(prev => {
      const newFilters = { ...prev, q: value || undefined }
      console.log('Debug - new filters:', newFilters)
      return newFilters
    })
    setPage(0)
  }

  // Gestionează filtrele
  const handleFilterChange = (field: keyof StudyFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value || undefined }))
    setPage(0)
  }

  // Resetează filtrele
  const resetFilters = () => {
    setFilters({})
    setSortBy('created_at') // Reset la sortare implicită după data intrării în RIS
    setSortOrder('desc') // Reset la descendent (cea mai nouă prima)
    setPage(0)
    setDateRange(null)
    setSelectedStatuses([])
    setSelectedModalities([])
    setSearchQuery('')
  }

  // Handler pentru toggle status
  const handleStatusToggle = (status: string) => {
    setSelectedStatuses(prev => {
      const newStatuses = prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
      
      // Update filters
      if (newStatuses.length > 0) {
        setFilters(prevFilters => ({ ...prevFilters, status: newStatuses.join(',') }))
      } else {
        setFilters(prevFilters => {
          const { status, ...rest } = prevFilters
          return rest
        })
      }
      
      setPage(0)
      return newStatuses
    })
  }

  // Handler pentru toggle modality
  const handleModalityToggle = (modality: string) => {
    setSelectedModalities(prev => {
      const newModalities = prev.includes(modality)
        ? prev.filter(m => m !== modality)
        : [...prev, modality]
      
      // Update filters
      if (newModalities.length > 0) {
        setFilters(prevFilters => ({ ...prevFilters, modality: newModalities.join(',') }))
      } else {
        setFilters(prevFilters => {
          const { modality, ...rest } = prevFilters
          return rest
        })
      }
      
      setPage(0)
      return newModalities
    })
  }

  // Handler pentru search query cu DEBOUNCE (400ms)
  // ✨ OPTIMIZARE: Previne request-uri la fiecare tastă, așteaptă 400ms de pauză
  const handleSearchQueryChange = (query: string) => {
    // Actualizează UI imediat (responsive)
    setSearchQuery(query)
    
    // Debounce: anulează timeout-ul anterior și programează unul nou
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      // Actualizează filtrele după 400ms de pauză
      setFilters(prev => ({ ...prev, q: query || undefined }))
      setPage(0)
    }, 400)
  }

  // Handler pentru date range
  const handleDateRangeChange = (range: { start: string; end: string } | null) => {
    setDateRange(range)
    
    // Actualizează filters cu date range
    if (range) {
      // Convertim la format YYYY-MM-DD pentru backend
      const startDate = new Date(range.start)
      const endDate = new Date(range.end)
      
      const formatDateForAPI = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      
      // Setăm filtrul bazat pe tipul de dată selectat
      const dateField = dateType === 'study_date' ? 'study_date' 
        : dateType === 'update_date' ? 'updated_at'
        : dateType === 'received_date' ? 'received_date'
        : 'report_date'
      
      setFilters(prev => ({
        ...prev,
        [`${dateField}_start`]: formatDateForAPI(startDate),
        [`${dateField}_end`]: formatDateForAPI(endDate),
      }))
    } else {
      // Șterge toate filtrele de dată
      setFilters(prev => {
        const { 
          study_date_start, study_date_end,
          updated_at_start, updated_at_end,
          received_date_start, received_date_end,
          report_date_start, report_date_end,
          ...rest 
        } = prev
        return rest
      })
    }
    
    setPage(0)
  }


  // ✨ Verifică dacă un studiu este vizualizat ca coordonator (view-only)
  // Un medic coordonator vede studiul în mod view-only dacă:
  // 1. Este coordonator pe instituția studiului, ȘI
  // 2. NU este medicul asignat pe studiu
  const isCoordinatorViewOnly = (study: Study): boolean => {
    if (!isCoordinator || user?.role !== 'medic') return false
    
    // Verifică dacă studiul este din instituție coordonată
    const studyInstitutionId = study.institution_id || (study as any).institution?.id
    const isFromCoordinatedInstitution = studyInstitutionId && 
      coordinatedInstitutionIds.includes(studyInstitutionId)
    
    // Verifică dacă medicul curent NU este asignat pe studiu
    const isNotAssigned = study.assigned_to !== user?.id && study.doctor_id !== user?.id
    
    return isFromCoordinatedInstitution && isNotAssigned
  }

  // Formatează data și ora studiului (din DICOM - ora locală stocată ca UTC)
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    
    // Handle Orthanc date format (YYYYMMDD) - doar data, fără oră
    if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
      const year = dateString.substring(0, 4)
      const month = dateString.substring(4, 6)
      const day = dateString.substring(6, 8)
      return `${day}.${month}.${year}`
    }
    
    // Handle ISO date format - folosim UTC pentru a evita conversie timezone
    // (ora din DICOM e ora locală, stocată ca UTC în DB)
    try {
      const date = new Date(dateString)
      const day = date.getUTCDate().toString().padStart(2, '0')
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
      const year = date.getUTCFullYear()
      const hours = date.getUTCHours()
      const minutes = date.getUTCMinutes()
      
      // Afișăm ora doar dacă e diferită de 00:00 (studii care au StudyTime)
      if (hours === 0 && minutes === 0) {
        return `${day}.${month}.${year}`
      }
      
      // Returnează format DD.MM.YYYY HH:MM (24h)
      return `${day}.${month}.${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    } catch (error) {
      return dateString
    }
  }

  // Formatează numele pacientului
  const formatPatientName = (study: Study) => {
    if (study.patient_name) return study.patient_name
    if (study.patient_id) return `Pacient ${study.patient_id}`
    return 'Necunoscut'
  }

  // Normalizează statusul pentru afișare (folosește workflow_status cu fallback pe status)
  const normalizeStatus = (study: Study | { status?: string; workflow_status?: string }) => {
    return getStatusDisplay(study).label
  }

  // Obține culoarea pentru border status (folosește workflow_status cu fallback pe status)
  const getStatusBorderColor = (study: Study | { status?: string; workflow_status?: string }) => {
    return getStatusDisplay(study).bgColor
  }

  // Obține culoarea pentru prioritate
  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'error'
      case 'high':
        return 'warning'
      case 'normal':
        return 'success'
      case 'low':
        return 'info'
      default:
        return 'default'
    }
  }

  // Deschide direct OHIF viewer pentru studiu
  const handleViewStudy = (study: Study) => {
    if (study.study_uid) {
      // Deschide OHIF Viewer în popup maximized
      void openOHIFViewer(study.study_uid)
    } else {
      toast.error('StudyUID nu este disponibil pentru acest studiu')
    }
  }

  // Deschide în Weasis desktop viewer
  const handleOpenWeasis = async (study: Study) => {
    if (!study.study_uid) {
      toast.error('StudyUID nu este disponibil')
      return
    }
    try {
      const token = localStorage.getItem('accessToken') || ''
      const response = await fetch(
        `/api/v1/public/weasis/launch?study_uid=${study.study_uid}&token=${encodeURIComponent(token)}`
      )
      if (!response.ok) {
        throw new Error('Failed to get Weasis URL')
      }
      const data = await response.json()
      // Redirect direct către protocolul weasis://
      window.location.href = data.weasis_url
      toast.success('ZenRIS Desktop App se deschide...', { duration: 2000 })
    } catch (error) {
      console.error('Weasis launch error:', error)
      toast.error('Nu s-a putut deschide ZenRIS Desktop App')
    }
  }

  // Închide detaliile studiului
  const handleCloseStudyDetails = () => {
    setSelectedStudy(null)
    setShowStudyDetails(false)
  }

  // Actualizează un studiu specific în listă
  const updateStudyInList = (studyId: string, updates: Partial<Study>) => {
    setStudies(prevStudies => 
      prevStudies.map(study => 
        study.id === studyId ? { ...study, ...updates } : study
      )
    )
    // Actualizează și selectedStudy dacă este același
    if (selectedStudy?.id === studyId) {
      setSelectedStudy(prev => prev ? { ...prev, ...updates } : prev)
    }
  }

  // Deschide editorul de rapoarte pentru studiu
  const handleOpenReportEditor = async (
    study: Study,
    forceReadOnly: boolean = false,
    allowFinalizedEdit: boolean = false
  ) => {
    // ✨ Determină dacă e mod view-only (coordonator vede raportul altui medic)
    const viewOnlyMode = forceReadOnly || isCoordinatorViewOnly(study)
    setReportEditorReadOnly(viewOnlyMode)
    
    // Verifică dacă studiul este finalizat
    if (isFinalized(study) && !viewOnlyMode && !allowFinalizedEdit) {
      toast.error('Raportul este finalizat și nu poate fi editat. Folosiți funcția de amendare din pagina de rapoarte.')
      return
    }
    
    setSelectedStudy(study)
    
    // ✅ FIX: Fetch raportul pentru a avea textul complet
    try {
      console.log('Fetching report for study:', study.id, 'viewOnlyMode:', viewOnlyMode)
      // Pentru view-only, căutăm și rapoarte finale
      const statusFilter = viewOnlyMode ? '' : '&status=draft'
      const response = await apiClient.get(`/reports/?studyId=${study.id}${statusFilter}`)
      
      if (response.data.reports && response.data.reports.length > 0) {
        const report = response.data.reports[0]
        console.log('Found existing report:', report.id, 'with', report.html?.length || 0, 'chars')
        setCurrentReport(report)
      } else {
        console.log('No existing report found - will create new')
        setCurrentReport(null)
      }
    } catch (error) {
      console.error('Error fetching report:', error)
      setCurrentReport(null)
    }
    
    setShowReportEditor(true)
  }

  // View raport finalizat (PDF)
  const handleViewFinalizedReport = async (study: Study) => {
    try {
      // Caută raportul finalizat pentru studiu
      const response = await apiClient.get(`/reports/?studyId=${study.id}&status=final`)
      
      if (response.data.reports && response.data.reports.length > 0) {
        const report = response.data.reports[0]
        // Obține URL-ul PDF-ului
        const pdfResponse = await apiClient.get(`/reports/${report.id}/pdf-url`)
        if (pdfResponse.status === 200 && pdfResponse.data.pdf_url) {
          setPdfViewerUrl(pdfResponse.data.pdf_url)
        } else {
          toast.error('Raportul nu are un PDF generat')
        }
      } else {
        toast.error('Nu există raport finalizat pentru acest studiu')
      }
    } catch (error) {
      console.error('Error fetching finalized report:', error)
      toast.error('Eroare la încărcarea raportului')
    }
  }

  // Creează amendare pentru raport finalizat și deschide editorul pe draft-ul nou
  const handleAmendFromWorklist = async (study: Study) => {
    try {
      const shouldAmend = window.confirm(
        `Se va crea o versiune nouă draft pentru amendare.\n\nPacient: ${formatPatientName(study)}\nStudiu: ${study.description || 'N/A'}\n\nContinuați?`
      )
      if (!shouldAmend) return

      const reportListResponse = await apiClient.get(`/reports/?studyId=${study.id}&status=final`)
      const finalReport = reportListResponse.data?.reports?.[0]

      if (!finalReport?.id) {
        toast.error('Nu există raport finalizat pentru acest studiu')
        return
      }

      const fullReportResponse = await apiClient.get(`/reports/${finalReport.id}`)
      const fullReport = fullReportResponse.data
      const reportText = fullReport.editor_html || fullReport.html || ''

      if (!reportText || reportText.trim() === '') {
        toast.error('Raportul nu conține text. Nu se poate crea amendare.')
        return
      }

      await apiClient.post(`/reports/${finalReport.id}/amend`, {
        reason: 'Amendare din worklist',
        editor_html: reportText,
      })

      toast.success('Amendare creată. Se deschide editorul.')
      await loadStudies()
      await handleOpenReportEditor(study, false, true)
    } catch (error: any) {
      console.error('Error amending report from worklist:', error)
      toast.error(error.response?.data?.detail || 'Eroare la amendarea raportului')
    }
  }

  // Deschide dialogul pentru asocierea medicului
  const handleAssignDoctor = (study: Study) => {
    setStudyToAssign(study)
    setSelectedDoctor(study.assigned_to || '')
    setShowAssignDialog(true)
    // Încarcă primii 15 medici când se deschide dialogul
    searchDoctorsFunction('')
  }

  // Asociază medicul la studiu
  const handleAssignStudy = async () => {
    if (!studyToAssign || !selectedDoctor) {
      toast.error('Selectează un medic')
      return
    }

    try {
              // console.log('🔗 Asociere medic:', { study: studyToAssign.study_uid, doctor: selectedDoctor })
      const loadingToast = toast.loading('Se asociază medicul...')
      
      // Folosește endpoint-ul PACS pentru asocierea studiului
      await apiClient.post(`/pacs/studies/${studyToAssign.study_uid}/assign`, { 
        doctor_id: selectedDoctor 
      })
      
      // console.log('✅ Asociere reușită:', response.data)
      toast.success('Medicul a fost asociat cu succes', { id: loadingToast })
      setShowAssignDialog(false)
      setStudyToAssign(null)
      setSelectedDoctor('')
      
      // Reîncarcă studiile pentru a reflecta schimbarea
      // console.log('🔄 Reîncărcare studii după asociere...')
      loadStudies()
    } catch (err: any) {
      console.error('❌ Eroare la asocierea medicului:', err)
      toast.error(err.response?.data?.detail || 'Eroare la asocierea medicului')
    }
  }

  // Deschide dialogul pentru dezasocierea medicului
  const handleUnassignDoctor = (study: Study) => {
    setStudyToUnassign(study)
    setUnassignReason('')
    setShowUnassignDialog(true)
  }

  // Dezasociază medicul de la studiu
  const handleUnassignStudy = async () => {
    if (!studyToUnassign || !unassignReason.trim()) {
      toast.error('Te rog introdu motivul dezasocierii')
      return
    }

    try {
      const loadingToast = toast.loading('Se dezasociază medicul...')
      
      // Folosește endpoint-ul pentru dezasocierea studiului
      await apiClient.post(`/studies/${studyToUnassign.id}/unassign`, { 
        reason: unassignReason.trim()
      })
      
      toast.success('Medicul a fost dezasociat cu succes', { id: loadingToast })
      setShowUnassignDialog(false)
      setStudyToUnassign(null)
      setUnassignReason('')
      
      // Reîncarcă studiile pentru a reflecta schimbarea
      loadStudies()
    } catch (err: any) {
      console.error('❌ Eroare la dezasocierea medicului:', err)
      toast.error(err.response?.data?.detail || 'Eroare la dezasocierea medicului')
    }
  }

  // Handler pentru deschiderea ShareLinkDialog (long-lived sharing)
  const handleOpenShareDialog = (study: Study) => {
    setStudyToShare(study)
    setShowShareLinkDialog(true)
  }

  const handleCloseShareDialog = () => {
    setShowShareLinkDialog(false)
    setStudyToShare(null)
  }

  // Quick Share - generează link instant, copiază în clipboard, afișează notificare
  const handleQuickShare = async (study: Study) => {
    if (!study?.study_uid) return

    try {
      const response = await apiClient.post(
        `/studies/${study.study_uid}/share-link`,
        null,
        {
          params: {
            ttl_days: 365, // 1 an valabilitate
            max_uses: null // nelimitat
          }
        }
      )

      const fullUrl = `${window.location.origin}${response.data.share_url}`
      
      // Copy to clipboard
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(fullUrl)
        } else {
          // Fallback pentru browsere mai vechi
          const textArea = document.createElement('textarea')
          textArea.value = fullUrl
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }
        toast.success('Link generat și copiat în clipboard! Valabil 1 an.', {
          duration: 4000,
          icon: '🔗'
        })
      } catch (clipboardErr) {
        // Dacă clipboard nu funcționează, afișează link-ul pentru copiere manuală
        toast.success(
          `Link generat (copiere manuală): ${fullUrl}`,
          { duration: 10000 }
        )
      }
    } catch (err: any) {
      console.error('Eroare la generarea link-ului:', err)
      toast.error(err.response?.data?.detail || 'Eroare la generarea link-ului')
    }
  }

  // Upload document în studiu
  const handleUploadToStudy = (study: Study) => {
    setStudyToUpload(study)
    setShowUploadDialog(true)
  }

  const handleCloseUploadDialog = () => {
    setShowUploadDialog(false)
    setStudyToUpload(null)
    setUploadFile(null)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setUploadFile(file)
    }
  }

  const handleUploadDocument = async () => {
    if (!uploadFile || !studyToUpload) return

    try {
      setUploading(true)
      
      // Încarcă documentul direct în studiu (Orthanc)
      const formData = new FormData()
      formData.append('file', uploadFile)
      
      await apiClient.post(
        `/studies/${studyToUpload.study_uid}/attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      toast.success('Document încărcat cu succes în studiu!')
      handleCloseUploadDialog()
      
      // Reîncarcă studiile pentru a actualiza lista
      loadStudies()
      
    } catch (error: any) {
      console.error('Eroare la încărcarea documentului:', error)
      toast.error(error.response?.data?.detail || 'Eroare la încărcarea documentului')
    } finally {
      setUploading(false)
    }
  }

  // Toggle study selection
  const handleToggleStudy = (studyId: string) => {
    setSelectedStudies(prev => {
      const newSet = new Set(prev)
      if (newSet.has(studyId)) {
        newSet.delete(studyId)
      } else {
        newSet.add(studyId)
      }
      return newSet
    })
  }

  // Toggle all studies on current page
  const handleToggleAll = () => {
    if (selectedStudies.size === studies.length) {
      setSelectedStudies(new Set())
    } else {
      setSelectedStudies(new Set(studies.map(s => s.id)))
    }
  }

  // Download selected studies
  const handleDownloadSelected = async () => {
    if (selectedStudies.size === 0) {
      toast.error('Selectați cel puțin un studiu')
      return
    }

    const studiesToDownload = studies.filter(s => selectedStudies.has(s.id))
    
    toast.loading(`Se descarcă ${studiesToDownload.length} studii...`, { id: 'download-multiple' })

    let successCount = 0
    let errorCount = 0

    for (const study of studiesToDownload) {
      try {
        await handleDownloadStudy(study)
        successCount++
      } catch (error) {
        errorCount++
      }
      // Pauză scurtă între download-uri pentru a nu supraîncărca
      await new Promise(resolve => setTimeout(resolve, 500))
    }

    if (errorCount === 0) {
      toast.success(`${successCount} studii descărcate cu succes!`, { id: 'download-multiple' })
    } else {
      toast.warning(`${successCount} descărcate, ${errorCount} erori`, { id: 'download-multiple' })
    }
    
    // Clear selection after download
    setSelectedStudies(new Set())
  }

  // Download study as ZIP from Orthanc (streaming nativ, fără buffering în frontend)
  const handleDownloadStudy = async (study: Study) => {
    if (!study.study_uid) {
      toast.error('Studiul nu are UID valid pentru download')
      return
    }

    try {
      toast.loading('Se pregătește download-ul...', { id: `dl-${study.id}` })

      // 1) Cere URL semnat (token short-lived) - request autenticat (cookies)
      const { data } = await apiClient.post<{ url: string }>(
        `/pacs/studies/${study.study_uid}/download-token`
      )

      // 2) Pornește download-ul nativ (streaming începe imediat, inclusiv la studii mari)
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = data.url
      document.body.appendChild(iframe)

      // Cleanup iframe (nu oprește download-ul deja inițiat)
      setTimeout(() => {
        try {
          document.body.removeChild(iframe)
        } catch {
          // ignore
        }
      }, 15000)

      toast.success('Download inițiat!', { id: `dl-${study.id}` })
    } catch (error: any) {
      console.error('Eroare download:', error)
      toast.error('Eroare la download', { id: `dl-${study.id}` })
    }
  }

  // Hard delete studiu - DOAR pentru admin
  const handleHardDeleteStudy = (study: Study) => {
    setStudyToHardDelete(study)
    setHardDeleteReason('')
    setShowHardDeleteDialog(true)
  }

  const handleCloseHardDeleteDialog = () => {
    setShowHardDeleteDialog(false)
    setStudyToHardDelete(null)
    setHardDeleteReason('')
  }

  const handleConfirmHardDelete = async () => {
    if (!studyToHardDelete || !hardDeleteReason.trim()) {
      toast.error('Motivul ștergerii este obligatoriu (minim 10 caractere)')
      return
    }

    if (hardDeleteReason.trim().length < 10) {
      toast.error('Motivul trebuie să aibă cel puțin 10 caractere')
      return
    }

    try {
      setHardDeleting(true)
      const loadingToast = toast.loading('Se șterge studiul permanent...')

      const result = await medicApi.hardDeleteStudy(studyToHardDelete.id, hardDeleteReason.trim())

      // Afișează rezultatele ștergerii
      const deletedCount = Object.values(result.deleted_entities).reduce((sum: number, count: number) => sum + count, 0)
      
      let message = `Studiu șters permanent! Au fost șterse ${deletedCount} înregistrări asociate.`
      if (result.warnings.length > 0) {
        message += `\n\nAvertismente:\n${result.warnings.join('\n')}`
      }

      toast.success(message, { 
        id: loadingToast,
        duration: 8000 
      })

      handleCloseHardDeleteDialog()
      
      // Reîncarcă studiile pentru a actualiza lista
      loadStudies()
      
    } catch (error: any) {
      console.error('Eroare la ștergerea studiului:', error)
      const errorMessage = error.response?.data?.detail || 'Eroare la ștergerea studiului'
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Eroare la ștergerea studiului')
    } finally {
      setHardDeleting(false)
    }
  }

  // Actions pentru PageHeader - now simplified, filters moved to FiltersBar
  const headerActions = null // All actions now in FiltersBar

  return (
    <Box>
      {/* Unified Page Header */}
      <PageHeader
        title="Studii"
        actions={headerActions}
      />

      {/* ✨ SCALABLE SYNC v2.0 - Progress Dialog */}
      <SyncProgressDialog
        open={syncProgressOpen}
        taskIds={syncTaskIds}
        onClose={handleSyncProgressClose}
        onComplete={handleSyncComplete}
      />

      <Box sx={{ px: 3, py: 0.6 }}>
        {/* New Filters Bar */}
        <FiltersBar
          dateType={dateType}
          onDateTypeChange={setDateType}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          selectedStatuses={selectedStatuses}
          availableStatuses={availableStatuses}
          onStatusToggle={handleStatusToggle}
          selectedModalities={selectedModalities}
          availableModalities={availableModalities}
          onModalityToggle={handleModalityToggle}
          searchQuery={searchQuery}
          onSearchChange={handleSearchQueryChange}
          selectedInstitution={selectedInstitution}
          availableInstitutions={availableInstitutions}
          onInstitutionChange={(institutionId) => {
            setSelectedInstitution(institutionId)
            setPage(0)
          }}
          showDoctorFilter={user?.role ? ['admin', 'operator', 'tehnician'].includes(user.role) : false}
          doctorOptions={filterDoctors}
          doctorValue={filterDoctors.find(d => d.id === filters.doctor_id) || null}
          doctorInputValue={filterDoctorInput}
          loadingDoctorOptions={loadingFilterDoctors}
          onDoctorInputChange={(value, reason) => {
            setFilterDoctorInput(value)
            // evităm buclele: căutăm doar la typing real / clear
            if (reason === 'input') scheduleSearchDoctorsForFilter(value)
            if (reason === 'clear') scheduleSearchDoctorsForFilter('')
          }}
          onDoctorChange={(value) => {
            const doctorId = value ? value.id : ''
            handleFilterChange('doctor_id', doctorId)

            // menține opțiunea selectată în lista de options
            if (value) {
              setFilterDoctors(prev => (prev.some(d => d.id === value.id) ? prev : [value, ...prev]))
              const label =
                `${value.doctor_profile?.first_name || ''} ${value.doctor_profile?.last_name || ''}`.trim() ||
                value.email ||
                ''
              setFilterDoctorInput(label)
            } else {
              setFilterDoctorInput('')
            }
          }}
          onRefresh={loadStudies}
          onSync={syncStudiesFromPacs}
          onDownloadSelected={handleDownloadSelected}
          onShare={() => {
            if (selectedStudies.size === 0) {
              toast.error('Selectați cel puțin un studiu')
              return
            }
            
            if (selectedStudies.size > 1) {
              toast('Share multiplu în lucru - momentan doar un studiu odată', { icon: 'ℹ️' })
              return
            }
            
            // Pentru un singur studiu, generează link instant
            const studyId = Array.from(selectedStudies)[0]
            const study = studies.find(s => s.id === studyId)
            if (study) {
              handleQuickShare(study)
            }
          }}
          isRefreshing={loading}
          isSyncing={syncing}
          canSync={user?.role === 'admin'}
          canShare={user?.role ? ['medic', 'operator', 'admin', 'tehnician'].includes(user.role) : false}
          selectedCount={selectedStudies.size}
          showQuickActions={user?.role ? ['admin', 'operator', 'tehnician'].includes(user.role) : false}
          canAddPatient={user?.role ? ['admin', 'operator'].includes(user.role) : false}
          canDownload={user?.role ? ['admin', 'operator', 'medic'].includes(user.role) : false}
          onUploadDicom={() => setShowDicomUploadDialog(true)}
          onAddPatient={() => setShowAddPatientDialog(true)}
        />

        {/* Old filters - hidden for now, keep for reference */}
        {false && showFilters && (
        <Card sx={{ mb: 1 }}>
          <CardContent sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={2.5}>
                <TextField
                  fullWidth
                  label="Căutare"
                  placeholder="Caută după pacient, descriere..."
                  value={filters.q || ''}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1.8}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {availableStatuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1.8}>
                <FormControl fullWidth>
                  <InputLabel>Modalitate</InputLabel>
                  <Select
                    value={filters.modality || ''}
                    label="Modalitate"
                    onChange={(e) => handleFilterChange('modality', e.target.value)}
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {availableModalities.map((modality) => (
                      <MenuItem key={modality} value={modality}>
                        {modality}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {user?.role && ['admin', 'operator'].includes(user.role) && (
                <Grid item xs={12} md={2.2}>
                  <Autocomplete
                    fullWidth
                    options={filterDoctors}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option
                      return `${option.doctor_profile?.first_name || ''} ${option.doctor_profile?.last_name || ''}`.trim() || option.email || ''
                    }}
                    loading={loadingFilterDoctors}
                    inputValue={filterDoctorInput}
                    onInputChange={(_event, value, reason) => {
                      setFilterDoctorInput(value)
                      // IMPORTANT: MUI Autocomplete declanșează onInputChange și la 'reset'
                      // (ex. când selectezi o opțiune). Ca să evităm buclele, căutăm doar la typing real.
                      if (reason === 'input') {
                        scheduleSearchDoctorsForFilter(value)
                      }
                      if (reason === 'clear') {
                        scheduleSearchDoctorsForFilter('')
                      }
                    }}
                    onChange={(_, value) => {
                      const doctorId = value && typeof value !== 'string' ? value.id : undefined
                      handleFilterChange('doctor_id', doctorId || '')

                      // Menține opțiunea selectată în lista de options (altfel poate dispărea la următorul fetch)
                      if (value && typeof value !== 'string') {
                        setFilterDoctors(prev => (prev.some(d => d.id === value.id) ? prev : [value, ...prev]))
                        const label =
                          `${value.doctor_profile?.first_name || ''} ${value.doctor_profile?.last_name || ''}`.trim() ||
                          value.email ||
                          ''
                        setFilterDoctorInput(label)
                      } else {
                        setFilterDoctorInput('')
                      }
                    }}
                    value={filterDoctors.find(d => d.id === filters.doctor_id) || null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Medic"
                        placeholder="Caută medic..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingFilterDoctors ? <CircularProgress size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText="Nu s-au găsit medici"
                  />
                </Grid>
              )}
              <Grid item xs={12} md={1.7}>
                <FormControl fullWidth>
                  <InputLabel>Prioritate</InputLabel>
                  <Select
                    value={filters.priority || ''}
                    label="Prioritate"
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {availablePriorities.map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={resetFilters}
                    size="small"
                  >
                    Resetează
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Mesaj de eroare */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Afișare studiilor cu paginare */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mt: 0 }}>
        <TableContainer>
          <Table
            stickyHeader
            size="small"
            sx={{
              '& .MuiTableCell-root': {
                py: 0.2,
              },
              '& .MuiTypography-root': {
                fontSize: '0.84rem', // +20%
                lineHeight: 1.15,
              },
              '& .MuiChip-root': {
                height: 17, // +20%
                fontSize: '0.78rem', // +20%
              },
              '& .MuiCheckbox-root': {
                p: 0,
              },
              '& .MuiCheckbox-root .MuiSvgIcon-root': {
                fontSize: 16,
              },
              // Micșorează icon-urile din tabel (~30%) fără să afecteze topbar/filtre
              '& .MuiTableBody-root .MuiIconButton-root': {
                p: '2px',
              },
              '& .MuiTableBody-root .MuiIconButton-root .MuiSvgIcon-root': {
                fontSize: 15,
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    padding: '6px 12px',
                    height: '34px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Checkbox
                    indeterminate={selectedStudies.size > 0 && selectedStudies.size < studies.length}
                    checked={studies.length > 0 && selectedStudies.size === studies.length}
                    onChange={handleToggleAll}
                    size="small"
                  />
                </TableCell>
                <TableCell 
                  onClick={handlePatientNameSort}
                  sx={{ 
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      transition: 'background-color 0.2s'
                    }
                  }}
                  title={`Sort după nume pacient ${sortBy === 'patient_name' ? (sortOrder === 'desc' ? '(desc)' : '(asc)') : '(click pentru sortare)'}`}
                >
                  Pacient {sortBy === 'patient_name' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                </TableCell>
                <TableCell>CNP</TableCell>
                <TableCell 
                  onClick={handleDateHeaderClick}
                  sx={{ 
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      transition: 'background-color 0.2s'
                    }
                  }}
                  title={`Sort după dată intrare RIS ${sortBy === 'created_at' ? (sortOrder === 'desc' ? '(desc)' : '(asc)') : '(click pentru sortare)'}`}
                >
                  Data Intrare RIS {sortBy === 'created_at' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                </TableCell>
                <TableCell sx={{ maxWidth: '300px', width: '300px' }}>Descriere</TableCell>
                <TableCell>Modalitate</TableCell>
                <TableCell>Serii</TableCell>
                <TableCell>Segmente</TableCell>
                {(user?.role !== 'medic' || isCoordinator) && <TableCell>Medic Asociat</TableCell>}
                <TableCell>Status</TableCell>
                {selectedInstitution === 'all' && <TableCell>Instituție</TableCell>}
                <TableCell>Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={(user?.role === 'medic' && !isCoordinator) ? (selectedInstitution === 'all' ? 11 : 10) : (selectedInstitution === 'all' ? 12 : 11)} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : combinedStudies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={(user?.role === 'medic' && !isCoordinator) ? (selectedInstitution === 'all' ? 11 : 10) : (selectedInstitution === 'all' ? 12 : 11)} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nu s-au găsit studii
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                combinedStudies.map((study) => (
                  <React.Fragment key={study.id}>
                    <TableRow
                      hover
                      sx={{
                        height: '26px', // +20%
                        '& td': {
                          padding: '1px 10px', // +20% spacing
                          fontSize: '0.84rem', // +20%
                          lineHeight: 1.15,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ width: 40 }}>
                        <Checkbox
                          checked={selectedStudies.has(study.id)}
                          onChange={() => handleToggleStudy(study.id)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* Status Icon - Verde pentru complete, Spinner galben pentru incoming */}
                          {study.is_incoming ? (
                            <Tooltip title="Studiu în curs de transfer...">
                              <HourglassTopIcon 
                                sx={{ 
                                  fontSize: 18, 
                                  color: '#fbbf24',
                                  animation: 'spin 1.5s linear infinite',
                                  '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                  }
                                }} 
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Studiu complet în RIS">
                              <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                            </Tooltip>
                          )}
                          <Box>
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              onClick={() => !study.is_incoming && study.patient_id && navigate(`/patients/${study.patient_id}`)}
                              sx={{
                                cursor: (!study.is_incoming && study.patient_id) ? 'pointer' : 'default',
                                color: study.is_incoming ? 'text.secondary' : (study.patient_id ? 'primary.main' : 'inherit'),
                                opacity: study.is_incoming ? 0.7 : 1,
                                '&:hover': (!study.is_incoming && study.patient_id) ? {
                                  textDecoration: 'underline'
                                } : {}
                              }}
                            >
                              {formatPatientName(study)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {study.patient_cnp || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon fontSize="inherit" color="action" sx={{ fontSize: 16 }} />
                          {formatDate(study.created_at)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: '300px', width: '300px' }}>
                        <Tooltip 
                          title={study.description || 'Fără descriere'} 
                          arrow
                          placement="top"
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {study.description || 'Fără descriere'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={study.modalities_list && study.modalities_list.length > 0 ? study.modalities_list.join(', ') : 'N/A'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {study.series_count || 0}/{study.instances_count || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {segmentCounts[study.id] !== undefined ? (
                          segmentCounts[study.id] > 0 ? (
                            <Chip 
                              label={segmentCounts[study.id]} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                              sx={{ height: '20px', fontSize: '0.7rem', minWidth: '28px' }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">0</Typography>
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Box>
                    </TableCell>
                    {(user?.role !== 'medic' || isCoordinator) && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {study.assigned_doctor_name || 'Neasociat'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={normalizeStatus(study)}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: getStatusBorderColor(study),
                          borderWidth: '1.5px',
                          color: 'text.primary',
                          backgroundColor: 'transparent',
                          minWidth: '85px',
                          height: 18,
                          fontSize: '0.7rem',
                          justifyContent: 'center'
                        }}
                      />
                    </TableCell>
                    {selectedInstitution === 'all' && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {study.institution_name || 'N/A'}
                        </Typography>
                      </TableCell>
                    )}
                      {/* Acțiuni - diferite per rol și disable pentru incoming */}
                      {study.is_incoming ? (
                        // Incoming: acțiuni dezactivate - doar afișează mesaj
                        <TableCell>
                          <Tooltip title="Studiu în curs de transfer - acțiunile vor fi disponibile după finalizare">
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.disabled', 
                                fontStyle: 'italic',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <HourglassTopIcon sx={{ fontSize: 14 }} />
                              În transfer...
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      ) : user?.role === 'tehnician' ? (
                        // Tehnician: doar butonul de expand segmente
                        <TableCell>
                          <Tooltip title={expandedStudyId === study.id ? "Ascunde segmente" : "Arată segmente"}>
                            <IconButton
                              size="small"
                              onClick={() => setExpandedStudyId(expandedStudyId === study.id ? null : study.id)}
                            >
                              {expandedStudyId === study.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      ) : (
                        // Alte roluri: acțiuni complete
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Tooltip title="Deschide ZenRIS WebViewer">
                              <IconButton
                                size="small"
                                onClick={() => handleViewStudy(study)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Deschide ZenRIS Desktop App">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenWeasis(study)}
                                sx={{ color: '#5c6bc0' }}
                              >
                                <DesktopIcon />
                              </IconButton>
                            </Tooltip>
                            {isFinalized(study) ? (
                              <>
                                <Tooltip title="Vizualizare raport finalizat">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewFinalizedReport(study)}
                                  >
                                    <DescriptionIcon />
                                  </IconButton>
                                </Tooltip>
                                {user?.role && ['admin', 'operator'].includes(user.role) && (
                                  <Tooltip title="Amendare raport finalizat">
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() => handleAmendFromWorklist(study)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            ) : isCoordinatorViewOnly(study) ? (
                              // ✨ Coordonator: poate vizualiza dar nu edita
                              <Tooltip title="Vizualizare raport (read-only)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenReportEditor(study, true)}
                                  sx={{ color: 'info.main' }}
                                >
                                  <DescriptionIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Editor rapoarte">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenReportEditor(study)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {user?.role !== 'medic' && (
                              <>
                                {!study.assigned_to ? (
                                  <Tooltip title="Asociază medic">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAssignDoctor(study)}
                                    >
                                      <AssignIcon />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Dezasociază medic">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleUnassignDoctor(study)}
                                      color="warning"
                                    >
                                      <UnassignIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            )}
                            {user?.role === 'admin' && (
                              <Tooltip title="Șterge permanent studiul (IREVERSIBIL)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleHardDeleteStudy(study)}
                                  color="error"
                                  sx={{ ml: 1 }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {/* ✨ Segment Expand Button - la sfârșit în dreapta */}
                            <Tooltip title={expandedStudyId === study.id ? "Ascunde segmente" : "Arată segmente"}>
                              <IconButton
                                size="small"
                                onClick={() => setExpandedStudyId(expandedStudyId === study.id ? null : study.id)}
                                sx={{ ml: 1 }}
                              >
                                {expandedStudyId === study.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                    {/* Segment Collapse - full width row */}
                    <TableRow>
                      <TableCell colSpan={(user?.role === 'medic' && !isCoordinator) ? (selectedInstitution === 'all' ? 11 : 10) : (selectedInstitution === 'all' ? 12 : 11)} sx={{ p: 0, borderTop: 'none' }}>
                        <SegmentAccordion
                          studyId={study.id}
                          studyUid={study.study_uid}
                          pacsUrl={study.pacs_base_url}
                          onSegmentUpdate={() => {
                            loadStudies()
                            fetchSegmentCounts([study.id])
                          }}
                          expanded={expandedStudyId === study.id}
                          onToggle={() => setExpandedStudyId(expandedStudyId === study.id ? null : study.id)}
                          refreshKey={study.assigned_to || ''}
                          hideActions={user?.role === 'tehnician'}
                        />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Paginare */}
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rânduri per pagină:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} din ${count !== -1 ? count : `mai mult de ${to}`}`
          }
        />
      </Paper>

      {/* Dialog pentru detaliile studiului */}
      <Dialog
        open={showStudyDetails}
        onClose={handleCloseStudyDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalii Studiu
        </DialogTitle>
        <DialogContent>
          {selectedStudy && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Pacient
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatPatientName(selectedStudy)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  ID Pacient
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedStudy.patient_id || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  CNP
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedStudy.patient_cnp || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Data Intrare RIS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatDate(selectedStudy.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Descriere
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedStudy.study_description || 'Fără descriere'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Modalitate
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedStudy.modality || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={normalizeStatus(selectedStudy)}
                  size="small"
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderColor: getStatusBorderColor(selectedStudy),
                    borderWidth: '1.5px',
                    color: 'text.primary',
                    backgroundColor: 'transparent',
                    minWidth: '85px',
                    justifyContent: 'center'
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Prioritate
                </Typography>
                <Chip
                  label={selectedStudy.priority || 'Normal'}
                  color={getPriorityColor(selectedStudy.priority) as any}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Sursă
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedStudy.pacs_source || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Instituție
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedStudy.institution_name || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Atribuit la
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedStudy.assigned_to || 'Neatribuit'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStudyDetails}>
            Închide
          </Button>
                     {selectedStudy && (
             <Button
               variant="contained"
               startIcon={<EditIcon />}
               onClick={() => handleOpenReportEditor(selectedStudy)}
               disabled={isFinalized(selectedStudy)}
               sx={{
                 opacity: isFinalized(selectedStudy) ? 0.6 : 1
               }}
             >
               {isFinalized(selectedStudy) ? 'Raport Finalizat' : 'Editor Rapoarte'}
             </Button>
           )}
                 </DialogActions>
       </Dialog>

       {/* Dialog pentru asocierea medicului */}
       <Dialog
         open={showAssignDialog}
         onClose={() => setShowAssignDialog(false)}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>
           Asociază medic la studiu
         </DialogTitle>
         <DialogContent>
           {studyToAssign && (
             <Box sx={{ mt: 2 }}>
               <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                 Studiu: {studyToAssign.study_description}
               </Typography>
               <Typography variant="body2" color="text.secondary" gutterBottom>
                 Pacient: {studyToAssign.patient_name}
               </Typography>
               
               <Autocomplete
                 fullWidth
                 sx={{ mt: 2 }}
                 options={searchDoctors}
                 getOptionLabel={(option) => {
                   const firstName = option.doctor_profile?.first_name || 'Necunoscut'
                   const lastName = option.doctor_profile?.last_name || 'Necunoscut'
                   return `${firstName} ${lastName} (${option.email})`
                 }}
                 loading={loadingDoctors}
                 onInputChange={(_event, newInputValue, reason) => {
                   // evită request-uri la 'reset' (ex. când selectezi o opțiune)
                   if (reason === 'input') searchDoctorsFunction(newInputValue)
                   if (reason === 'clear') searchDoctorsFunction('')
                 }}
                 onChange={(_event, newValue) => {
                   setSelectedDoctor(newValue?.id || '')
                 }}
                 renderInput={(params) => (
                   <TextField
                     {...params}
                     label="Caută medicul"
                     placeholder="Începe să scrii numele medicului..."
                     InputProps={{
                       ...params.InputProps,
                       endAdornment: (
                         <>
                           {loadingDoctors ? <CircularProgress color="inherit" size={20} /> : null}
                           {params.InputProps.endAdornment}
                         </>
                       ),
                     }}
                   />
                 )}
                 noOptionsText="Nu s-au găsit medici"
                 loadingText="Se caută..."
               />
             </Box>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setShowAssignDialog(false)}>
             Anulează
           </Button>
           <Button
             variant="contained"
             onClick={handleAssignStudy}
             disabled={!selectedDoctor}
           >
             Asociază
           </Button>
         </DialogActions>
       </Dialog>

       {/* Dialog pentru dezasocierea medicului */}
       <Dialog
         open={showUnassignDialog}
         onClose={() => setShowUnassignDialog(false)}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>
           Dezasociază medic de la studiu
         </DialogTitle>
         <DialogContent>
           {studyToUnassign && (
             <Box sx={{ mt: 2 }}>
               <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                 Studiu: {studyToUnassign.description || studyToUnassign.study_description}
               </Typography>
               <Typography variant="body2" color="text.secondary" gutterBottom>
                 Pacient: {studyToUnassign.patient_name}
               </Typography>
               <Typography variant="body2" color="text.secondary" gutterBottom>
                 Medic curent: {studyToUnassign.assigned_doctor_name}
               </Typography>
               
               <TextField
                 fullWidth
                 multiline
                 rows={3}
                 label="Motivul dezasocierii *"
                 value={unassignReason}
                 onChange={(e) => setUnassignReason(e.target.value)}
                 placeholder="Introdu motivul pentru care dezasociezi medicul de la acest studiu..."
                 sx={{ mt: 2 }}
                 required
               />
             </Box>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setShowUnassignDialog(false)}>
             Anulează
           </Button>
           <Button
             variant="contained"
             color="warning"
             onClick={handleUnassignStudy}
             disabled={!unassignReason.trim()}
           >
             Dezasociază
           </Button>
         </DialogActions>
       </Dialog>

       {/* Dialog pentru editorul de rapoarte - Consistent styling */}
       {selectedStudy && showReportEditor && (
         <Dialog
           open={showReportEditor}
           onClose={() => setShowReportEditor(false)}
           maxWidth="xl"
           fullWidth
           PaperProps={{
             sx: { 
               height: '90vh',
               bgcolor: 'background.paper',
               backgroundImage: 'none',
             }
           }}
         >
           <DialogTitle sx={{ py: 1, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
             {reportEditorReadOnly ? 'Vizualizare Raport' : 'Editor Raport'} - {selectedStudy.patient_name}
             {reportEditorReadOnly && (
               <Chip 
                 label="View Only" 
                 size="small" 
                 color="info" 
                 sx={{ ml: 2, height: 20, fontSize: '0.7rem' }}
               />
             )}
           </DialogTitle>
          <DialogContent sx={{ p: 1, bgcolor: 'background.default' }}>
            <ReportEditor
              reportId={currentReport?.id}
              studyId={selectedStudy.id}
              initialContent={currentReport?.editor_html || currentReport?.html || ''}
              initialJson={currentReport?.editor_json}
              status={currentReport?.status || 'draft'}
              readOnly={reportEditorReadOnly}
              patientInfo={{
                name: selectedStudy.patient_name || '',
                cnp: selectedStudy.patient_cnp || '',
                dob: (selectedStudy as any).patient_dob || '',
                sex: (selectedStudy as any).patient_sex || 'N/A'
              }}
              studyInfo={{
                description: selectedStudy.study_description || '',
                date: selectedStudy.study_date || '',
                modality: selectedStudy.modalities_list?.join(', ') || selectedStudy.modality || ''
              }}
              onSave={async (_data) => {
                 toast.success('Raport salvat cu succes')
                 if (selectedStudy) {
                   updateStudyInList(selectedStudy.id, { 
                     status: 'alocat (draft)',
                     workflow_status: StudyWorkflowStatus.IN_PROGRESS,
                     assigned_to: user?.id || selectedStudy.assigned_to
                   })
                 }
               }}
               onFinalize={async () => {
                 setShowReportEditor(false)
                 toast.success('Raport finalizat cu succes')
                 if (selectedStudy) {
                   updateStudyInList(selectedStudy.id, { 
                     status: 'alocat (finalizat)',
                     workflow_status: StudyWorkflowStatus.COMPLETED
                   })
                 }
               }}
             />
           </DialogContent>
           <DialogActions sx={{ py: 0.5, px: 2, borderTop: '1px solid', borderColor: 'divider' }}>
             <Button onClick={() => setShowReportEditor(false)} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Închide</Button>
           </DialogActions>
         </Dialog>
       )}

       {/* Dialog pentru upload document în studiu */}
       <Dialog
         open={showUploadDialog}
         onClose={handleCloseUploadDialog}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>
           Încarcă Document în Studiu
         </DialogTitle>
         <DialogContent>
           {studyToUpload && (
             <Box sx={{ mt: 2 }}>
               <Typography variant="body1" sx={{ mb: 2 }}>
                 <strong>Studiu:</strong> {studyToUpload.study_description || 'N/A'}
               </Typography>
               <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                 <strong>Pacient:</strong> {studyToUpload.patient_name || 'N/A'}
               </Typography>
               
               <Box sx={{ mb: 2 }}>
                 <input
                   accept=".pdf,.jpg,.jpeg,.png,.stl,.docx,.xlsx,.zip"
                   style={{ display: 'none' }}
                   id="upload-file-input"
                   type="file"
                   onChange={handleFileSelect}
                 />
                 <label htmlFor="upload-file-input">
                   <Button
                     variant="outlined"
                     component="span"
                     startIcon={<UploadIcon />}
                     fullWidth
                   >
                     Selectează Fișier
                   </Button>
                 </label>
               </Box>
               
               {uploadFile && (
                 <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                   <Typography variant="body2">
                     <strong>Fișier selectat:</strong> {uploadFile.name}
                   </Typography>
                   <Typography variant="body2" color="text.secondary">
                     Dimensiune: {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                   </Typography>
                 </Box>
               )}
             </Box>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={handleCloseUploadDialog}>
             Anulează
           </Button>
           <Button
             variant="contained"
             onClick={handleUploadDocument}
             disabled={!uploadFile || uploading}
             startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
           >
             {uploading ? 'Se încarcă...' : 'Încarcă Document'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* Dialog pentru hard delete studiu - DOAR pentru admin */}
       <Dialog
         open={showHardDeleteDialog}
         onClose={handleCloseHardDeleteDialog}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle sx={{ color: 'error.main' }}>
           ⚠️ Șterge Permanent Studiul
         </DialogTitle>
         <DialogContent>
           {studyToHardDelete && (
             <Box sx={{ mt: 2 }}>
               <Alert severity="error" sx={{ mb: 3 }}>
                 <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                   ATENȚIE: Această operație este IREVERSIBILĂ!
                 </Typography>
                 <Typography variant="body2" sx={{ mt: 1 }}>
                   Vor fi șterse permanent:
                 </Typography>
                 <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                   <li>Studiul și toate seriile asociate</li>
                   <li>Rapoartele în lucru (draft)</li>
                   <li>Asocierile cu medicii</li>
                   <li>Documentele atașate studiului</li>
                   <li>Toate datele de audit pentru acest studiu</li>
                 </ul>
                 <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.dark' }}>
                   Rapoartele finalizate și billing-ul confirmat vor BLOCA ștergerea!
                 </Typography>
               </Alert>

               <Typography variant="body1" sx={{ mb: 2 }}>
                 <strong>Studiu:</strong> {studyToHardDelete.study_description || studyToHardDelete.study_uid}
               </Typography>
               <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                 <strong>Pacient:</strong> {studyToHardDelete.patient_name || 'N/A'}
               </Typography>
               
               <TextField
                 fullWidth
                 multiline
                 rows={3}
                 label="Motivul ștergerii (obligatoriu - minim 10 caractere)"
                 value={hardDeleteReason}
                 onChange={(e) => setHardDeleteReason(e.target.value)}
                 placeholder="Ex: Studiu duplicat, eroare de sincronizare, cerere pacient, etc."
                 error={hardDeleteReason.length > 0 && hardDeleteReason.length < 10}
                 helperText={
                   hardDeleteReason.length > 0 && hardDeleteReason.length < 10 
                     ? `Încă ${10 - hardDeleteReason.length} caractere necesare`
                     : `${hardDeleteReason.length}/500 caractere`
                 }
                 inputProps={{ maxLength: 500 }}
               />
             </Box>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={handleCloseHardDeleteDialog}>
             Anulează
           </Button>
           <Button
             variant="contained"
             color="error"
             onClick={handleConfirmHardDelete}
             disabled={!hardDeleteReason.trim() || hardDeleteReason.trim().length < 10 || hardDeleting}
             startIcon={hardDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
           >
             {hardDeleting ? 'Se șterge...' : 'Șterge Permanent'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* ShareLinkDialog - Long-lived sharing (1 an) */}
       <ShareLinkDialog 
         open={showShareLinkDialog}
         onClose={handleCloseShareDialog}
         study={studyToShare}
       />

       {/* Dialog pentru vizualizarea PDF raport finalizat */}
       {pdfViewerUrl && (
         <Dialog
           open={true}
           onClose={() => setPdfViewerUrl(null)}
           maxWidth="xl"
           fullWidth
           PaperProps={{
             sx: { height: '90vh' }
           }}
         >
           <DialogTitle>
             Vizualizare PDF Raport
           </DialogTitle>
           <DialogContent sx={{ p: 0, height: '100%' }}>
             <object
               data={pdfViewerUrl}
               type="application/pdf"
               style={{
                 width: '100%',
                 height: '100%',
                 border: 'none'
               }}
               title="PDF Viewer"
             >
               <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, flexDirection: 'column' }}>
                 <Typography variant="body2" color="text.secondary" gutterBottom>
                   PDF-ul nu poate fi afișat în browser.
                 </Typography>
                 <Button
                   variant="contained"
                   href={pdfViewerUrl}
                   target="_blank"
                   rel="noopener noreferrer"
                 >
                   Descarcă PDF
                 </Button>
               </Box>
             </object>
           </DialogContent>
          <DialogActions>
             <Button onClick={() => setPdfViewerUrl(null)}>Închide</Button>
           </DialogActions>
         </Dialog>
       )}

       {/* Dialog Încarcă DICOM extern */}
       {showDicomUploadDialog && (
         <UploadExternalDICOMDialog
           open={true}
           onClose={() => setShowDicomUploadDialog(false)}
           onSuccess={() => {
             setShowDicomUploadDialog(false)
             loadStudies() // Refresh list after upload
             toast.success('DICOM încărcat cu succes!')
           }}
           institutions={availableInstitutions}
         />
       )}

       {/* Dialog Adaugă Pacient */}
       {user?.tenant_id && (
         <QuickAddPatientForm
           open={showAddPatientDialog}
           onClose={() => setShowAddPatientDialog(false)}
           onSuccess={() => {
             setShowAddPatientDialog(false)
             toast.success('Pacient adăugat cu succes!')
           }}
           tenantId={user.tenant_id}
           institutionId={user.institution_id || user.tenant_id}
         />
       )}
      </Box>
    </Box>
  )
}

export default StudiesPage
